import React, {useContext, useEffect, useState} from "react";

// Services
import useCountryAxiosInstance from "../../../hooks/useCountryAxiosInstance";
import {NotificationsContext} from "../../../ui-components/Notifications";
import {useIntl} from "react-intl";

// Components
import KpiProductBarChart from "../../../components/Charts/KpiProductBarChart";
import {PriceSelector, DimensionFilter, lkpField} from "./Products.lib";
import Placeholder from "../../../ui-components/Placeholder";
import ProductTrendChart from "./ProductTrendChart";
import ProductsDetailTable from "./DetailTable";

// Types
import {AnalyticsProductsProps, ProductRow, Filters, Dimension, TrendData, GeoData} from "./Products.type";
import SearchSelect from "../../../ui-components/SearchSelect";
import {statusOptions} from "../Worksites/Worksite";
import UsersHeatMap from "../Users/UsersHeatMap";
import CountryContext from "../../../CountryContext";
import {WorksiteTerminatedFilter} from "../WorksiteTerminatedFilter";

export function AnalyticsProducts(
  {
    visibilityUserIds,
    analysisPeriod,
    worksiteTypes,
    worksitesOwnershipFilter
  }: AnalyticsProductsProps
) {

  const intl = useIntl();
  const [loading, setLoading] = useState(true);
  const {push} = useContext(NotificationsContext);
  const {country} = useContext(CountryContext);
  const countryApiRequest = useCountryAxiosInstance();

  const [showPrice, setShowPrice] = useState<boolean>(true);
  const [products, setProducts] = useState<ProductRow[] | undefined>(undefined);
  const [filters, setFilters] = useState<Filters>({classes: [], lines: [], products: []});
  const [selectedStatus, setSelectedStatus] = useState<{ label: string; value: string }[]>([]);

  const [chartData, setChartData] = useState<ProductRow[] | undefined>(undefined);
  const [trend, setTrend] = useState<TrendData[] | undefined>(undefined)
  const [geo, setGeo] = useState<GeoData[] | undefined>(undefined)
  const [worksiteTerminationFilter, setWorksiteTerminationFilter] = useState<string>()
  const [worksiteTerminationReasonFilter, setWorksiteTerminationReasonFilter] = useState<string>()


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const payload = {
          start_date: analysisPeriod.start,
          end_date: analysisPeriod.end,
          visibility_user_ids: visibilityUserIds,
          filter_status: selectedStatus.length ? selectedStatus.map(i => i.value) : undefined,
          ...(filters.lines.length && {lines: filters.lines.map(i => i.value)}),
          ...(filters.classes.length && {classes: filters.classes.map(i => i.value)}),
          ...(filters.products.length && {products: filters.products.map(i => i.value)}),
          worksites_ownership: worksitesOwnershipFilter.length ? worksitesOwnershipFilter : undefined,
          ...(worksiteTerminationFilter ? { worksite_termination: worksiteTerminationFilter } : {}),
          ...(worksiteTerminationReasonFilter ? { worksite_termination_reason: worksiteTerminationReasonFilter } : {}),
        }
        const [res] = await Promise.all([
          // @ts-ignore
          countryApiRequest.post("/analytics/products", payload)

        ]);
        // @ts-ignore
        setProducts(res.products)
        // @ts-ignore
        setTrend(res.trend)
        // @ts-ignore
        setGeo(res.geo)

      } catch (error) {
        console.log(error)
        setProducts([])
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
      } finally {
        setLoading(false)
      }
    };

    fetchData()

  }, [visibilityUserIds, analysisPeriod, selectedStatus, filters, visibilityUserIds, worksiteTerminationFilter, worksiteTerminationReasonFilter]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (products) {
      let res = products
      Object.entries(lkpField).forEach(([k, v]) => {
        if (filters[k as Dimension].length) {
          // @ts-ignore
          res = res.filter(i => filters[k].map(j => j.value).includes(i[v]))
        }
      })
      setChartData(res)
    }
  }, [products, filters])

  return <div className="w-full">
    {!loading && products && chartData && worksiteTypes ?
      <div className="w-full flex flex-col items-center">
        {products.length ?
          <div className="w-full flex flex-col items-center">
            <div className="w-full flex flex-col md:flex-row items-center gap-3 my-4">
              {['lines', 'classes', 'products'].map(dimension =>
                <div key={dimension} className="w-full">
                  <DimensionFilter
                    intl={intl}
                    products={products}
                    filters={filters}
                    setFilters={setFilters}
                    dimension={dimension as Dimension}
                  />
                </div>
              )}
              <div className="w-full">
                <SearchSelect
                  label='Stato'
                  options={statusOptions.map(i => ({value: i, label: intl.formatMessage({id: i})}))}
                  onChange={(e: any) => setSelectedStatus(e)}
                  value={selectedStatus}
                  isMulti={true}
                  CustomGroup={undefined}
                  loadOptions={undefined}
                  CustomOption={undefined}
                />
              </div>
              <div className={"w-full"}>
                <WorksiteTerminatedFilter
                  worksiteTerminationFilter={worksiteTerminationFilter}
                  setWorksiteTerminationFilter={setWorksiteTerminationFilter}
                  worksiteTerminationReasonFilter={worksiteTerminationReasonFilter}
                  setWorksiteTerminationReasonFilter={setWorksiteTerminationReasonFilter}
                />
              </div>
            </div>
            <PriceSelector intl={intl} showPrice={showPrice} setShowPrice={setShowPrice}/>
            <div className="p-2 w-full">
              <KpiProductBarChart
                id="KpiProductBarChart"
                data={chartData}
                intl={intl}
                showPrice={showPrice}
              />
            </div>
            <div className="w-full flex flex-col lg:flex-row my-4 items-center mt-5">
              <div className={`w-full ${country.code === "it" ? "lg:w-2/3" : ""}`}>
                {trend ?
                  <div>
                    <ProductTrendChart
                      id={'productTrendChart'}
                      data={trend} intl={intl}
                      selectedStatus={selectedStatus}
                    />
                  </div> :
                  <div className="p-8">
                    <Placeholder height="h-60"/>
                  </div>
                }
              </div>
              {country.code === "it" && (
                <div className="w-full lg:w-1/3">
                  {geo ?
                    <UsersHeatMap
                      id="UsersHeatMap"
                      data={geo.map(i => ({value: i.amt_total, id: 'IT-' + i.cod_province}))}
                    /> :
                    <div className="p-8">
                      <Placeholder height="h-60"/>
                    </div>
                  }
                </div>
              )}
            </div>
            <div className="mt-8 w-full">
              <ProductsDetailTable
                intl={intl}
                push={push}
                countryApiRequest={countryApiRequest}
                analysisPeriod={analysisPeriod}
                selectedStatus={selectedStatus}
                visibilityUserIds={visibilityUserIds}
                worksiteTypes={worksiteTypes}
                products={products}
                mainFilters={filters}
                worksitesOwnershipFilter={worksitesOwnershipFilter}
                worksiteTerminationFilter={worksiteTerminationFilter}
                worksiteTerminationReasonFilter={worksiteTerminationReasonFilter}
              />
            </div>
          </div> :
          <span className="text-lg text-gray-500 my-32">
            {intl.formatMessage({id: "no_data_available"})}
          </span>
        }
      </div> :
      <div className="flex flex-col w-full mb-4 gap-y-5 mt-10">
        <div className="flex flex-row gap-x-5">
          <Placeholder width="w-1/2" height="h-12"/>
          <Placeholder width="w-1/2" height="h-12"/>
        </div>
        <div className="flex flex-row gap-x-5">
          <Placeholder width="w-full" height="h-80"/>
        </div>
      </div>
    }
  </div>
}